import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  PhotoIcon,
  UserIcon,
  StarIcon,
  ChevronLeftIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  ArrowUpTrayIcon
} from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';

const RosterManagement = () => {
  const { sportId } = useParams();
  const [sport, setSport] = useState(null);
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingPlayer, setEditingPlayer] = useState(null);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    jerseyNumber: '',
    email: '',
    phone: '',
    parentName: '',
    parentPhone: '',
    parentEmail: '',
    emergencyContact: '',
    emergencyPhone: '',
    medicalNotes: '',
    bio: '',
    isCaptain: false,
    isStarter: false,
    profileImage: null
  });

  useEffect(() => {
    fetchSportData();
    fetchPlayers();
  }, [sportId]);

  const fetchSportData = async () => {
    try {
      const response = await fetch(`/api/sports/id/${sportId}`);
      if (response.ok) {
        const data = await response.json();
        setSport(data.sport);
      } else {
        // Mock data fallback
        setSport({
          id: sportId,
          name: 'Football',
          slug: 'football',
          coachName: 'Coach Johnson'
        });
      }
    } catch (error) {
      console.error('Error fetching sport:', error);
    }
  };

  const fetchPlayers = async () => {
    try {
      setLoading(true);
      const response = await fetch(`/api/players/sport/${sportId}`);
      if (response.ok) {
        const data = await response.json();
        setPlayers(data.players || []);
      } else {
        // Mock data fallback
        loadMockPlayers();
      }
    } catch (error) {
      console.error('Error fetching players:', error);
      loadMockPlayers();
    } finally {
      setLoading(false);
    }
  };

  const loadMockPlayers = () => {
    const mockPlayers = [
      {
        id: 1,
        firstName: 'John',
        lastName: 'Smith',
        jerseyNumber: 12,
        position: 'Quarterback',
        grade: '12',
        height: '6\'2"',
        weight: 185,
        email: 'john.smith@student.eastendprep.edu',
        phone: '(555) 123-4567',
        parentName: 'Robert Smith',
        parentPhone: '(555) 123-4568',
        isCaptain: true,
        isStarter: true,
        profileImage: '/api/placeholder/150/150',
        bio: 'Senior quarterback with excellent leadership skills.'
      },
      {
        id: 2,
        firstName: 'Mike',
        lastName: 'Johnson',
        jerseyNumber: 24,
        position: 'Running Back',
        grade: '11',
        height: '5\'10"',
        weight: 175,
        email: 'mike.johnson@student.eastendprep.edu',
        phone: '(555) 234-5678',
        parentName: 'Lisa Johnson',
        parentPhone: '(555) 234-5679',
        isCaptain: false,
        isStarter: true,
        profileImage: '/api/placeholder/150/150',
        bio: 'Junior running back known for speed and agility.'
      },
      {
        id: 3,
        firstName: 'David',
        lastName: 'Wilson',
        jerseyNumber: 88,
        position: 'Wide Receiver',
        grade: '10',
        height: '6\'0"',
        weight: 170,
        email: 'david.wilson@student.eastendprep.edu',
        phone: '(555) 345-6789',
        parentName: 'Sarah Wilson',
        parentPhone: '(555) 345-6790',
        isCaptain: false,
        isStarter: false,
        profileImage: '/api/placeholder/150/150',
        bio: 'Sophomore with great hands and route running.'
      }
    ];
    setPlayers(mockPlayers);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData(prev => ({
        ...prev,
        profileImage: file
      }));
    }
  };

  const handleSubmit = async (e) => {

    e.preventDefault();
    
    // Validate required fields
    if (!formData.firstName.trim() || !formData.lastName.trim()) {
      alert('First Name and Last Name are required.');
      return;
    }
    
    try {

      
      // Prepare data for submission
      const dataToSend = {
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        sportId: sportId
      };
      
      // Include other fields only if they have values
      Object.keys(formData).forEach(key => {
        if (!['firstName', 'lastName', 'profileImage'].includes(key) && formData[key] !== null && formData[key] !== '') {
          if (key === 'isCaptain' || key === 'isStarter') {
            dataToSend[key] = formData[key];
          } else {
            dataToSend[key] = formData[key];
          }
        }
      });
      


      const url = editingPlayer 
        ? `/api/players/${editingPlayer.id}`
        : '/api/players';
      
      const method = editingPlayer ? 'PUT' : 'POST';
      

      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify(dataToSend)
      });


      
      if (response.ok) {

        await fetchPlayers();
        resetForm();
        setShowAddModal(false);
        setEditingPlayer(null);
      } else {
        const errorText = await response.text();
        console.error('Error saving player:', response.status, errorText);
        alert(`Error saving player: ${response.status} - ${errorText}`);
      }
    } catch (error) {
      console.error('Error saving player:', error);
      alert(`Error saving player: ${error.message}`);
    }
  };

  const handleEdit = (player) => {
    setEditingPlayer(player);
    setFormData({
      firstName: player.firstName || '',
      lastName: player.lastName || '',
      jerseyNumber: player.jerseyNumber || '',
      email: player.email || '',
      phone: player.phone || '',
      parentName: player.parentName || '',
      parentPhone: player.parentPhone || '',
      parentEmail: player.parentEmail || '',
      emergencyContact: player.emergencyContact || '',
      emergencyPhone: player.emergencyPhone || '',
      medicalNotes: player.medicalNotes || '',
      bio: player.bio || '',
      isCaptain: player.isCaptain || false,
      isStarter: player.isStarter || false,
      profileImage: null
    });
    setShowAddModal(true);
  };

  const handleDelete = async (playerId) => {
    if (window.confirm('Are you sure you want to remove this player from the roster?')) {
      try {
        const response = await fetch(`/api/players/${playerId}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('authToken')}`
          }
        });

        if (response.ok) {
          await fetchPlayers();
        } else {
          console.error('Error deleting player');
        }
      } catch (error) {
        console.error('Error deleting player:', error);
      }
    }
  };

  const resetForm = () => {
    setFormData({
      firstName: '',
      lastName: '',
      jerseyNumber: '',
      email: '',
      phone: '',
      parentName: '',
      parentPhone: '',
      parentEmail: '',
      emergencyContact: '',
      emergencyPhone: '',
      medicalNotes: '',
      bio: '',
      isCaptain: false,
      isStarter: false,
      profileImage: null
    });
  };

  const filteredPlayers = players.filter(player => {
    const matchesSearch = 
      player.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      player.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      player.jerseyNumber.toString().includes(searchTerm);
    
    return matchesSearch;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white shadow-sm rounded-lg p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Link
              to="/admin/sports"
              className="p-2 text-gray-400 hover:text-gray-600"
            >
              <ChevronLeftIcon className="h-6 w-6" />
            </Link>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {sport?.name} Roster Management
              </h1>
              <p className="text-gray-600 mt-1">
                Manage players, positions, and team information
              </p>
            </div>
          </div>
          <button
            onClick={() => {
              resetForm();
              setEditingPlayer(null);
              setShowAddModal(true);
            }}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            Add Player
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white shadow-sm rounded-lg p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="relative">
            <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-3 text-gray-400" />
            <input
              type="text"
              placeholder="Search players..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            />
          </div>
          
          <div className="flex items-center text-sm text-gray-600">
            <FunnelIcon className="h-5 w-5 mr-2" />
            {filteredPlayers.length} of {players.length} players
          </div>
        </div>
      </div>

      {/* Players List */}
      <div className="bg-white shadow-sm rounded-lg">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Team Roster</h2>
        </div>
        
        {filteredPlayers.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Player
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Jersey #
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredPlayers.map((player) => (
                  <tr key={player.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-12 w-12">
                          {player.profileImage ? (
                            <img
                              className="h-12 w-12 rounded-full object-cover"
                              src={player.profileImage}
                              alt={`${player.firstName} ${player.lastName}`}
                            />
                          ) : (
                            <div className="h-12 w-12 rounded-full bg-gray-200 flex items-center justify-center">
                              <UserIcon className="h-6 w-6 text-gray-400" />
                            </div>
                          )}
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {player.firstName} {player.lastName}
                            {player.isCaptain && (
                              <StarIconSolid className="inline h-4 w-4 text-yellow-500 ml-1" />
                            )}
                          </div>
                          <div className="text-sm text-gray-500">{player.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      #{player.jerseyNumber}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleEdit(player)}
                          className="p-2 text-gray-400 hover:text-blue-600 transition-colors"
                          title="Edit Player"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(player.id)}
                          className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                          title="Delete Player"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-6 text-center text-gray-500">
            <UserIcon className="h-12 w-12 mx-auto mb-4 text-gray-300" />
            <p>No players found matching your criteria.</p>
          </div>
        )}
      </div>

      {/* Add/Edit Player Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-medium text-gray-900">
                  {editingPlayer ? 'Edit Player' : 'Add New Player'}
                </h3>
                <button
                  onClick={() => {
                    setShowAddModal(false);
                    setEditingPlayer(null);
                    resetForm();
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ×
                </button>
              </div>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Basic Information */}
                  <div className="space-y-4">
                    <h4 className="text-md font-medium text-gray-900">Basic Information</h4>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">First Name *</label>
                        <input
                          type="text"
                          name="firstName"
                          value={formData.firstName}
                          onChange={handleInputChange}
                          required
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Last Name *</label>
                        <input
                          type="text"
                          name="lastName"
                          value={formData.lastName}
                          onChange={handleInputChange}
                          required
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Jersey #</label>
                      <input
                        type="number"
                        name="jerseyNumber"
                        value={formData.jerseyNumber}
                        onChange={handleInputChange}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      />
                    </div>
                    

                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Profile Photo</label>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        className="mt-1 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                      />
                    </div>
                  </div>
                  
                  {/* Contact Information */}
                  <div className="space-y-4">
                    <h4 className="text-md font-medium text-gray-900">Contact Information</h4>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Student Email</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Student Phone</label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Parent/Guardian Name</label>
                      <input
                        type="text"
                        name="parentName"
                        value={formData.parentName}
                        onChange={handleInputChange}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      />
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Parent Phone</label>
                        <input
                          type="tel"
                          name="parentPhone"
                          value={formData.parentPhone}
                          onChange={handleInputChange}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Parent Email</label>
                        <input
                          type="email"
                          name="parentEmail"
                          value={formData.parentEmail}
                          onChange={handleInputChange}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                        />
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Emergency Contact</label>
                        <input
                          type="text"
                          name="emergencyContact"
                          value={formData.emergencyContact}
                          onChange={handleInputChange}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Emergency Phone</label>
                        <input
                          type="tel"
                          name="emergencyPhone"
                          value={formData.emergencyPhone}
                          onChange={handleInputChange}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                        />
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Additional Information */}
                <div className="space-y-4">
                  <h4 className="text-md font-medium text-gray-900">Additional Information</h4>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Medical Notes</label>
                    <textarea
                      name="medicalNotes"
                      value={formData.medicalNotes}
                      onChange={handleInputChange}
                      rows={2}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      placeholder="Any medical conditions, allergies, or special considerations..."
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Player Bio</label>
                    <textarea
                      name="bio"
                      value={formData.bio}
                      onChange={handleInputChange}
                      rows={3}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      placeholder="Brief description of the player's strengths, achievements, etc..."
                    />
                  </div>
                  
                  <div className="flex space-x-6">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        name="isCaptain"
                        checked={formData.isCaptain}
                        onChange={handleInputChange}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label className="ml-2 block text-sm text-gray-900">
                        Team Captain
                      </label>
                    </div>
                    
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        name="isStarter"
                        checked={formData.isStarter}
                        onChange={handleInputChange}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label className="ml-2 block text-sm text-gray-900">
                        Starting Player
                      </label>
                    </div>
                  </div>
                </div>
                
                {/* Form Actions */}
                <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddModal(false);
                      setEditingPlayer(null);
                      resetForm();
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                  >
                    {editingPlayer ? 'Update Player' : 'Add Player'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RosterManagement;